import { memo, useEffect, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconFoldersOutline18 as Folders } from "nucleo-ui-outline-18"
import { z } from "zod"

import {
	Button,
	Dialog,
	Form,
	FormInput,
	FormSelect,
	ProjectIconKeys,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { IconPicker } from "@/components/color-picker"
import { mutateOk, useZero } from "@/lib/zero"

interface UpdateTagDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	values: UpdateTagFormValues
}

const UpdateTagFormSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	tagGroupId: z.string().min(1),
	icon: z.nativeEnum(ProjectIconKeys),
})
type UpdateTagFormValues = z.infer<typeof UpdateTagFormSchema>

export const UpdateTagDialog = memo(
	({ isOpen, onOpenChange, values }: UpdateTagDialogProps) => {
		const zero = useZero()

		const [tagGroups] = useQuery(queries.tagGroups())

		const form = useZodForm({
			schema: UpdateTagFormSchema,
			defaultValues: {
				...values,
			},
		})
		useEffect(() => {
			form.reset(values)
		}, [values, form])
		const iconWatch = form.watch("icon")
		const tagGroupIdWatch = form.watch("tagGroupId")
		const selectedTagGroup = useMemo(
			() => tagGroups.find((g) => g.id === tagGroupIdWatch),
			[tagGroupIdWatch, tagGroups],
		)
		const options = useMemo(() => {
			return tagGroups.map((g) => ({ label: g.name, value: g.id }))
		}, [tagGroups])
		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				title="Update tag"
				modal={false}
				withOverlay={true}
				// Opened from the row's right-click menu, which hands focus back to the row as it closes.
				onInteractOutside={(event) => {
					if (event.type === "dismissableLayer.focusOutside") {
						event.preventDefault()
					}
				}}
				footer={
					<Button form="update-opportunity-form" type="submit">
						Update
					</Button>
				}
			>
				<Form
					id="update-opportunity-form"
					form={form}
					className="space-y-4"
					onSubmit={async (values) => {
						const groupColor =
							tagGroups.find((g) => g.id === values.tagGroupId)?.color ?? ""
						await mutateOk(
							zero.mutate(
								mutators.tags.update({
									id: values.id,
									color: groupColor,
									icon: values.icon,
									name: values.name,
									tagGroupId: values.tagGroupId,
								}),
							),
						)
						onOpenChange(false)
					}}
				>
					<FormInput name="name" label="Name" />
					<div className="flex items-end gap-4">
						<IconPicker
							icon={iconWatch}
							onIconChange={(icon) => {
								form.setValue("icon", icon)
							}}
							color={selectedTagGroup?.color}
						/>
						<FormSelect
							className="flex-1"
							Icon={Folders}
							showErrorMessage={false}
							options={options}
							name="tagGroupId"
						/>
					</div>
				</Form>
			</Dialog>
		)
	},
)
